import { observable, action, runInAction, makeObservable } from 'mobx';
import { injectable, inject } from '@servicetitan/react-ioc';
import { SupportedPlaidInterface, PlaidIntegrations } from '../utils/plaid-integrations-service';
import { Billing, PlaidBankAccountInfo } from '../api/internal-billing.api';

declare global {
    interface Window {
        App: any;
    }
}

@injectable()
export class AchModalStore {
    @observable isLoading = false;
    @observable modalOpen = false;
    @observable scriptFailure = false;
    @observable salesForceIframeOrigin = window.App.IsGoInstance
        ? 'https://servicetitan.my.salesforce-sites.com'
        : 'https://uat1-servicetitan.cs63.force.com';
    @observable
    salesforceIframeSrc =
        `${this.salesForceIframeOrigin}/processor/SBPlaidLinkPaymentPage?expTok=c6f1499f-b5c4-463d-a529-ff486776ef08&sfBillToId=${window.App.billToAccountId}&email=`;
    @observable isVisibleSFIframe = false;
    @observable accountEmail = '';

    private instance: any;

    constructor(
        @inject(Billing) private billingApiStore: Billing,
        @inject(PlaidIntegrations) private plaidIntegrations: PlaidIntegrations
    ) {
        makeObservable(this);
    }

    @action
    openModal = () => {
        this.modalOpen = true;
    };

    @action
    closeModal = () => {
        this.modalOpen = false;
    };

    @action
    initWithSalesforceFG = async () => {
        this.isLoading = true;
        try {
            const plaidCredentials = (await this.billingApiStore.getPlaidCredentials()).data;
            runInAction(() => {
                if (!plaidCredentials?.Email) {
                    this.scriptFailure = true;
                } else {
                    this.scriptFailure = false;
                    this.accountEmail = plaidCredentials.Email;
                }
            });
        } catch (e) {
            runInAction(() => {
                this.scriptFailure = true;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @action
    initialize = async (events: SupportedPlaidInterface) => {
        this.isLoading = true;
        try {
            if (!this.instance) {
                this.instance = await this.plaidIntegrations.getPlaidInstance(events);
                runInAction(() => {
                    this.scriptFailure = false;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.scriptFailure = true;
                this.instance = undefined;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    execute = (events?: SupportedPlaidInterface) => async () => {
        if (window.App.Features.SalesforceIntegration) {
            this.updateSFIframeVisibility(true);
            this.updateLoadingState(true);
        } else {
            if (!this.instance) {
                this.instance = await this.plaidIntegrations.getPlaidInstance(events!);
            }
            this.closeModal();
            this.instance.open();
        }
    };

    @action
    updateLoadingState = (value: boolean) => {
        this.isLoading = value;
    };

    @action
    updateSFIframeVisibility = (isVisible: boolean) => {
        this.isVisibleSFIframe = isVisible;
    };

    @action
    resetInstance = () => {
        this.instance = null;
    };

    @action
    updateBankAccount = async (
        bankAccountInfo: PlaidBankAccountInfo,
        events?: SupportedPlaidInterface
    ) => {
        try {
            this.isLoading = true;
            const details = await this.billingApiStore.addOrUpdateBankAccount(bankAccountInfo);

            if (!window.App.Features.SalesforceIntegration) {
                // Reset instance in case manual verificaton is required
                this.plaidIntegrations.resetPlaidIntegration();
                this.instance = await this.plaidIntegrations.getPlaidInstance(events!);
            }
            return details.data;
        } finally {
            this.closeModal();
            this.updateSFIframeVisibility(false);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };
}
